import { IOrbProps } from "./App";
import { useApi } from './components/useApi';
import { useUserContext } from './components/useUserContext';
import { useDataContext } from './components/useDataContext';
import { useIntegration } from './components/useIntegration';
import React, { useCallback, useEffect, useState } from "react";
import { HashRouter, Switch, Route } from 'react-router-dom';
import { ErrorBoundary } from "./components/ErrorBoundary";
import { IntegrationContext } from "./components/IntegrationContext";
import { DataContext } from "./components/DataContext";
import { OrbUserContext } from "./components/OrbUserContext";
import { CrLoadingOverlay } from './components/cr/CrLoadingOverlay';
import { AppGlobals, AppIDs } from "./AppGlobals";
import { withErrorHandling } from './components/withErrorHandling';
import { TrackRisksMenu } from "./components/TrackRisksMenu";
import RiskReportingAdmin from "./components/RiskReportingAdmin";
import { AdminRoute, NotAuthorised, NotFound, RiskAdminRoute } from "./Routes";
import LoadingWhite from "./LoadingWhite";
import RiskSignOff from "./components/risk/RiskSignOff";
import { TrackRisksWelcome } from "./components/TrackRisks/TrackRisksWelcome";
import { Insights } from "./components/Insights";
import { useContentStyle } from "./components/ContentStyleContext";
import SystemAdministration from "./components/SystemAdministration";
import TrackRisksReport from "./components/TrackRisks/TrackRisksReport";
import { TopBanner } from "./TopBanner";
import { MenuToggle } from "./components/MenuToggle";
import { IEntityDescription1, IRiskAppHeatMapColor } from "./types";
import { buildTrackRisksBreadcrumb } from "./breadcrumbs";
import { useHistory, useLocation } from "react-router-dom";
import { HeatMapConfig } from "./components/risk/HeatMapConfig";

const TrackRisksApp: React.FunctionComponent<IOrbProps> = (props: IOrbProps) => {

    const history = useHistory();
    const location = useLocation();

    const [entityDescriptionForMenu, setEntityDescriptionForMenu] = useState<IEntityDescription1>();
    const [riskScoreFormula, setRiskScoreFormula] = useState<string | null>(null);
    const [riskAppHeatMapColors, setRiskAppHeatMapColors] = useState<IRiskAppHeatMapColor[]>([]);
    const [riskScoreFormulaLoading, setRiskScoreFormulaLoading] = useState<boolean>(true);
    const { dataServices, errorHandling } = props;

    const apiConnected = useApi(dataServices);
    const uc = useUserContext(apiConnected, dataServices, errorHandling);
    const ld = useDataContext(apiConnected, dataServices, errorHandling);

    const loadInitialData = useCallback(async () => {
        try {
            const [formula, heatMapColors] = await Promise.all([
                dataServices.accountService?.getRiskAppScoreFormula(),
                dataServices.riskAppHeatMapColorService?.readSelectedBandColors()
            ]);

            //console.log('[TrackRisksApp] Loaded risk score formula', formula);
            //console.log('[TrackRisksApp] Loaded risk app heat map colors', heatMapColors);
            
            setRiskScoreFormula(formula ?? '(P * I)');
            setRiskAppHeatMapColors(heatMapColors ?? []);
        }
        catch (error) {
            console.error('[TrackRisksApp] Failed to load initial data', error);
            setRiskScoreFormula('(P * I)');
            setRiskAppHeatMapColors([]);
        }
        finally {
            setRiskScoreFormulaLoading(false);
        }
    }, [dataServices.accountService, dataServices.riskAppHeatMapColorService]);

    useEffect(() => {
        let cancelled = false;

        const runLoad = async () => {
            if (cancelled) {
                return;
            }
            await loadInitialData();
        };

        runLoad();

        return () => {
            cancelled = true;
        };
    }, [loadInitialData]);

    const integration = useIntegration({
        disableDirectorateManagement: false,
        disableGroupManagement: false,
        disablePartnerOrganisationManagement: false,
        disableProjectManagement: false,
        disableUserManagement: false,
        dataSourceName: ''
    });

    const routeProps = {
        ...props,
        menuData: props.menuData,
        apiConnected: apiConnected,
        entityDescriptionForMenu: entityDescriptionForMenu,
        onEntityDescriptionChange: setEntityDescriptionForMenu,
        riskScoreFormula,
        riskScoreFormulaLoading,
        riskAppHeatMapColors,
        onHeatMapConfigSaved: loadInitialData,
        ...uc,
        ...ld
    };

    const commonPropsUpdates = {
        emphasiseProjectsWithCustomField: 'SoS Priority Project',
        emphasisedProjectsHeaderText: 'SoS Priority Projects',
        otherProjectsHeaderText: 'All Other Reports',
        ...routeProps,
    };

    const [isNavigationVisible, setNavigationVisible] = useState(false);
    const toggleNavigation = () => setNavigationVisible(!isNavigationVisible);

    const { contentStyle } = useContentStyle();

    if (uc.userContext.UserId === null) return (<LoadingWhite />);

    const appName = props.menuData.filter(m => m.ID === AppIDs.RISKREG)[0].AppName;

    const breadcrumbItems = buildTrackRisksBreadcrumb({
        customerTitle: AppGlobals.CustomerTitle,
        appName,
        location,
        history
    });

    return (
        <React.Fragment>
            <ErrorBoundary>
                <IntegrationContext.Provider value={integration}>
                    <DataContext.Provider value={ld}>
                        <OrbUserContext.Provider value={uc}>

                            <div className="main">
                                <div className="container">

                                    {/* LEFT NAVIGATION (drawer) */}
                                    <div className={`navigation ${isNavigationVisible ? 'show' : ''}`}>
                                        {!uc.userContext.UserId
                                            ? <CrLoadingOverlay isLoading={true} />
                                            : <TrackRisksMenu {...routeProps} onLinkClicked={toggleNavigation} />
                                        }
                                    </div>

                                    {/* BACKDROP */}
                                    <div
                                        className={`nav-backdrop ${isNavigationVisible ? 'show' : ''}`}
                                        onClick={toggleNavigation}
                                        aria-hidden={!isNavigationVisible}
                                    />

                                    {/* RIGHT CONTENT AREA */}
                                    <div className="right-area">
                                        <div className="top-banner">
                                            <MenuToggle onToggleNavigation={toggleNavigation} />

                                            <TopBanner
                                                customerTitle={AppGlobals.CustomerTitle}
                                                userName={AppGlobals.UserTitle}
                                                breadcrumbItems={breadcrumbItems}
                                                onLogout={props.onLogout}
                                            />
                                        </div>

                                        <div className="content" style={contentStyle}>
                                            <HashRouter>
                                                <Switch>

                                                    <Route exact path="/a3/welcome" {...routeProps}>
                                                        <TrackRisksWelcome />
                                                    </Route>

                                                    <Route exact path="/a3/insights" {...routeProps}>
                                                        <Insights dashboardRef='RiskRegistersDashboard' />
                                                    </Route>

                                                    <RiskAdminRoute path="/a3/risks" {...routeProps}>
                                                        <RiskReportingAdmin {...routeProps} entity="Risks" />
                                                    </RiskAdminRoute>

                                                    <RiskAdminRoute
                                                        path="/a3/risks-with-filters/:probabilityFilter/:impactLevelFilter"
                                                        {...routeProps}
                                                    >
                                                        <RiskReportingAdmin {...routeProps} entity="RisksWithFilters" />
                                                    </RiskAdminRoute>

                                                    <RiskAdminRoute path="/a3/issues" {...routeProps}>
                                                        <RiskReportingAdmin {...routeProps} entity="Issues" />
                                                    </RiskAdminRoute>

                                                    <RiskAdminRoute path="/a3/issues-actions" {...routeProps}>
                                                        <RiskReportingAdmin {...routeProps} entity="IssuesActions" />
                                                    </RiskAdminRoute>

                                                    <RiskAdminRoute
                                                        path="/a3/issues-actions-for-issue/:issueIdForActions"
                                                        {...routeProps}
                                                    >
                                                        <RiskReportingAdmin {...routeProps} entity="IssuesActions" />
                                                    </RiskAdminRoute>

                                                    <RiskAdminRoute path="/a3/incidents" {...routeProps}>
                                                        <RiskReportingAdmin {...routeProps} entity="Incidents" />
                                                    </RiskAdminRoute>

                                                    <RiskAdminRoute path="/a3/incidents-actions" {...routeProps}>
                                                        <RiskReportingAdmin {...routeProps} entity="IncidentsActions" />
                                                    </RiskAdminRoute>

                                                    <RiskAdminRoute
                                                        path="/a3/incidents-actions-for-incident/:incidentIdForActions"
                                                        {...routeProps}
                                                    >
                                                        <RiskReportingAdmin {...routeProps} entity="IncidentsActions" />
                                                    </RiskAdminRoute>

                                                    <RiskAdminRoute path="/a3/risk-mitigating-actions" {...routeProps}>
                                                        <RiskReportingAdmin {...routeProps} entity="RiskMitigationActions" />
                                                    </RiskAdminRoute>

                                                    <RiskAdminRoute
                                                        path="/a3/sign-off-for-risk/:riskIdForUpdates/:probabilityFilter/:impactLevelFilter"
                                                        {...routeProps}
                                                    >
                                                        <RiskSignOff {...commonPropsUpdates} />
                                                    </RiskAdminRoute>

                                                    <RiskAdminRoute
                                                        path="/a3/sign-off-for-risk/:riskIdForUpdates"
                                                        {...routeProps}
                                                    >
                                                        <RiskSignOff {...commonPropsUpdates} />
                                                    </RiskAdminRoute>

                                                    <RiskAdminRoute
                                                        path="/a3/risk-mitigating-actions-for-risk/:riskIdForMitigatingActions/:probabilityFilter/:impactLevelFilter"
                                                        {...routeProps}
                                                    >
                                                        <RiskReportingAdmin {...routeProps} entity="RiskMitigationActions" />
                                                    </RiskAdminRoute>

                                                    <RiskAdminRoute
                                                        path="/a3/risk-mitigating-actions-for-risk/:riskIdForMitigatingActions"
                                                        {...routeProps}
                                                    >
                                                        <RiskReportingAdmin {...routeProps} entity="RiskMitigationActions" />
                                                    </RiskAdminRoute>

                                                    <AdminRoute path="/a3/settings/customFields" {...routeProps}>
                                                        <SystemAdministration {...routeProps} entity="CustomFields" />
                                                    </AdminRoute>

                                                    <AdminRoute path="/a3/settings/risk-types" {...routeProps}>
                                                        <SystemAdministration {...routeProps} entity="RiskTypes" />
                                                    </AdminRoute>

                                                    <AdminRoute path="/a3/settings/heatmap-config" {...routeProps}>
                                                        <HeatMapConfig {...routeProps} />
                                                    </AdminRoute>

                                                    <Route path="/a3/outputs" {...routeProps}>
                                                        <TrackRisksReport {...routeProps} />
                                                    </Route>

                                                    <Route path="/not-authorised">
                                                        <NotAuthorised />
                                                    </Route>

                                                    <Route path="*">
                                                        <NotFound />
                                                    </Route>

                                                </Switch>
                                            </HashRouter>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </OrbUserContext.Provider>
                    </DataContext.Provider>
                </IntegrationContext.Provider>
            </ErrorBoundary>
        </React.Fragment>
    );
};

export const TrackRisksAppWithErrorHandling =
    withErrorHandling(TrackRisksApp);
